import { useState } from "react";
import API from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { MapPin, Check, AlertTriangle, Home, Briefcase, MailWarning, HelpCircle } from "lucide-react";

/**
 * Address deliverability badge. Renders an inline pill describing the
 * Smarty Residential Delivery Indicator + record type. Hidden when
 * upstream provider is Mapbox (rdi="unknown") since Mapbox can't
 * classify.
 */
function DeliverabilityBadge({ rdi }) {
  if (!rdi || rdi === "unknown") return null;
  const map = {
    residential: {
      icon: Home,
      label: "Residential",
      cls: "bg-emerald-100 text-emerald-700 border border-emerald-300",
      help: "We can ship temperature-controlled medication here.",
    },
    commercial: {
      icon: Briefcase,
      label: "Commercial",
      cls: "bg-amber-100 text-amber-800 border border-amber-300",
      help: "Make sure someone is on-site Mon–Fri to receive a refrigerated package.",
    },
    po_box: {
      icon: MailWarning,
      label: "PO Box — not deliverable",
      cls: "bg-red-100 text-red-800 border border-red-300",
      help: "UPS/FedEx cannot deliver compounded medication to a PO Box. Please use a street address.",
    },
  };
  const meta = map[rdi] || { icon: HelpCircle, label: rdi, cls: "bg-slate-100 text-slate-700 border border-slate-300", help: "" };
  const Icon = meta.icon;
  return (
    <div className="mt-2" data-testid={`deliverability-badge-${rdi}`}>
      <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${meta.cls}`}>
        <Icon className="w-3.5 h-3.5" />
        {meta.label}
      </span>
      {meta.help && (
        <p className="text-xs text-slate-600 mt-1.5">{meta.help}</p>
      )}
    </div>
  );
}

/**
 * Address verification card. Hard-gates a parent form: you can't submit
 * until a click on "Verify Address" returns confidence ≥ high from Mapbox.
 *
 * Props:
 *   street, city, state, zip — current values from the parent form
 *   onVerified(normalized)   — fires once with the canonical USPS address;
 *                              parent should snap its fields to it
 *   onStatusChange(status)   — "pristine" | "verifying" | "verified" | "rejected"
 *                              parent uses this to disable its save button
 *   resetSignal              — change any value to flip status back to pristine
 *                              (e.g. parent passes Date.now() when an address
 *                              field gets edited)
 *   locked                   — boolean. When true, render the card in a
 *                              non-interactive "already verified across
 *                              sessions" state (no Verify button). Used after
 *                              the patient previously verified this exact
 *                              address.
 */
export default function AddressVerifyCard({
  street, city, state, zip,
  onVerified,
  onStatusChange,
  resetSignal,
  locked = false,
}) {
  const [status, setStatus] = useState("pristine");
  const [reason, setReason] = useState("");
  const [rdi, setRdi] = useState(null);   // residential | commercial | po_box | unknown
  // last resetSignal we saw — when it changes we go back to pristine.
  const [lastReset, setLastReset] = useState(resetSignal);
  if (resetSignal !== lastReset) {
    setLastReset(resetSignal);
    if (status !== "pristine") {
      setStatus("pristine");
      setReason("");
      setRdi(null);
      if (onStatusChange) onStatusChange("pristine");
    }
  }

  const setS = (s, r = "") => {
    setStatus(s);
    setReason(r);
    if (onStatusChange) onStatusChange(s);
  };

  const verify = async () => {
    const _street = (street || "").trim();
    const _city = (city || "").trim();
    const _state = (state || "").trim();
    const _zip = (zip || "").trim();
    if (!_street || !_city || !_state || !_zip) {
      toast.error("Please fill street, city, state, and ZIP first.");
      return;
    }
    const m = _street.match(/^(\S+)\s+(.+)$/);
    const street_number = m ? m[1] : "";
    const street_name = m ? m[2] : _street;
    setS("verifying");
    try {
      const { data } = await API.post("/address/validate", {
        street_number, street_name, city: _city, state: _state, postal_code: _zip,
      });
      if (data.verified) {
        const detectedRdi = data.normalized?.rdi || "unknown";
        setRdi(detectedRdi);
        // PO Boxes are validly USPS-listed but UNSHIPPABLE for our use case
        // (UPS/FedEx don't deliver, and compounded GLP-1 is temperature-
        // controlled). Block the save instead of celebrating "verified".
        if (detectedRdi === "po_box") {
          setS("rejected", "PO Box detected — we can't ship compounded medication to a PO Box. Please enter a street address.");
          toast.error("PO Box addresses are not deliverable.");
          return;
        }
        setS("verified");
        toast.success("Address verified");
        if (onVerified && data.normalized) onVerified(data.normalized);
      } else {
        setRdi(null);
        setS("rejected", data.reason || "Address could not be verified.");
        toast.error(data.reason || "Address could not be verified.");
      }
    } catch {
      setRdi(null);
      setS("rejected", "Could not reach the address verification service.");
      toast.error("Could not reach the address verification service. Please try again.");
    }
  };

  const tone =
    locked ? "bg-emerald-50 border-emerald-300"
    : status === "verified" ? "bg-emerald-50 border-emerald-300"
    : status === "rejected" ? "bg-red-50 border-red-300"
    : "bg-amber-50 border-amber-300";

  return (
    <div className={`rounded-xl border-2 p-4 transition-colors ${tone}`} data-testid="address-verify-card">
      <div className="flex items-start gap-3">
        <div className="flex-1">
          <p className="text-sm font-semibold text-slate-900 flex items-center gap-2">
            {locked ? <><Check className="w-4 h-4 text-emerald-600" /> Address verified <span className="text-xs font-normal text-emerald-700">(saved from your last visit)</span></>
            : status === "verified" ? <><Check className="w-4 h-4 text-emerald-600" /> Address verified</>
            : status === "rejected" ? <><AlertTriangle className="w-4 h-4 text-red-600" /> Address could not be verified</>
            : status === "verifying" ? <>Verifying…</>
            : <><MapPin className="w-4 h-4 text-amber-600" /> Address not yet verified</>}
          </p>
          <p className="text-xs text-slate-600 mt-1">
            {locked
              ? "Edit any address field above to re-verify."
              : status === "verified"
              ? "Address matches USPS records. You can save."
              : status === "rejected"
              ? (reason || "Please double-check the street, city, state, and ZIP.")
              : "Click below to confirm the address against USPS — required before save."}
          </p>
          {status === "verified" && <DeliverabilityBadge rdi={rdi} />}
        </div>
        {!locked && (
          <Button
            type="button"
            onClick={verify}
            disabled={status === "verifying"}
            size="sm"
            className="rounded-lg flex-shrink-0 bg-purple-600 hover:bg-purple-700 text-white"
            data-testid="verify-address-btn"
          >
            {status === "verifying" ? "Checking…"
             : status === "verified" ? "Re-verify"
             : "Verify Address"}
          </Button>
        )}
      </div>
    </div>
  );
}
